import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import FlyerSection from "@/components/FlyerSection";
import { BUSINESS, SERVICES, QUOTE_SERVICES, PROCESS, IMAGES } from "@/data/content";

const heroLines = ["We don't", "wash cars.", "We finish them."];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img
          src={IMAGES.hero}
          alt="Luxpress detailer inspecting a black BMW M2 under studio inspection lighting"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/35" aria-hidden="true" />
      <div className="relative mx-auto w-full max-w-7xl px-5 pb-20 pt-40 sm:px-8">
        <motion.p
          className="kicker mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
        >
          Luxpress Detailing — South Melbourne
        </motion.p>
        <h1 className="font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="hero-headline">
          {heroLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-2">
              <motion.span
                className={`block ${i === 2 ? "text-mint" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          className="mt-7 max-w-xl text-base leading-relaxed text-chrome sm:text-lg"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
        >
          Paint correction, ceramic coating and detailing for people who notice the difference.
          pH-neutral chemicals, premium products, and a finish you inspect under our lights — not yours.
        </motion.p>
        <motion.div
          className="mt-9 flex flex-col gap-4 sm:flex-row"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
        >
          <Link to="/contact" data-testid="hero-quote-cta" className="btn-primary w-full sm:w-auto">
            Request a quote <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link to="/pricing" data-testid="hero-pricing-cta" className="btn-ghost w-full sm:w-auto">
            See packages & pricing
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const ProofStrip = () => (
  <section className="border-b border-teal-line" data-testid="proof-strip">
    <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-teal-line lg:grid-cols-4">
      {[
        { top: `${BUSINESS.rating} ★`, sub: `${BUSINESS.reviewCount} Google reviews` },
        { top: "7 days", sub: "Open 9:30 AM – 6:00 PM" },
        { top: "South Melbourne", sub: "476 City Road, VIC 3205" },
        { top: "pH-neutral", sub: "Premium products only" },
      ].map((s, i) => (
        <Reveal key={s.top} delay={i * 0.08} className="px-5 py-8 sm:px-8">
          <p className="font-display text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">{s.top}</p>
          <p className="spec-label mt-2">{s.sub}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicesOverview = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="services-overview">
    <Reveal>
      <p className="kicker">Detailing packages</p>
      <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl">
        Real prices. Real text. No posters.
      </h2>
    </Reveal>
    <div className="mt-14 border-t border-teal-line">
      {SERVICES.map((s, i) => (
        <Reveal key={s.id} delay={i * 0.05}>
          <Link
            to={`/contact?service=${s.id}`}
            data-testid={`home-service-${s.id}`}
            className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-4 border-b border-teal-line py-6 transition-colors duration-300 hover:bg-panel sm:grid-cols-[3rem_1fr_auto_auto_auto] sm:gap-8 sm:px-4"
          >
            <span className="font-mono text-xs text-chrome/60">0{i + 1}</span>
            <span>
              <span className="block font-display text-lg font-bold uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-mint sm:text-2xl">{s.name}</span>
              <span className="mt-1 block text-sm text-chrome">{s.covers}</span>
            </span>
            <span className="hidden font-mono text-xs uppercase tracking-[0.18em] text-chrome sm:block">{s.duration}</span>
            <span className="font-mono text-sm text-mint sm:text-base">from ${s.price}</span>
            <ArrowUpRight className="hidden h-5 w-5 text-chrome transition-colors duration-300 group-hover:text-mint sm:block" aria-hidden="true" />
          </Link>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-chrome">
        Ceramic coating, PPF, tinting, chrome delete and caliper resprays are quoted per vehicle.
      </p>
      <Link to="/services" data-testid="home-all-services-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mint">
        All services <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
      </Link>
    </Reveal>
  </section>
);

const Transformation = () => (
  <section className="border-y border-teal-line bg-panel/40" data-testid="transformation-section">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <div className="grid items-end gap-8 lg:grid-cols-[1fr_auto]">
        <Reveal>
          <p className="kicker">The signature</p>
          <h2 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl">
            Drag the line.<br />That's the job.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-sm leading-relaxed text-chrome">
            Swirl marks and oxidation on the left. Corrected, coated paint on the right.
            This is the moment everything we do is built around.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="mt-12">
        <BeforeAfterSlider />
      </Reveal>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        {[
          { src: IMAGES.wheel, alt: "Audi RS wheel — black rim with red brake caliper" },
          { src: IMAGES.interior, alt: "Mercedes-AMG interior — carbon and perforated leather steering wheel over red leather" },
          { src: "/assets/lambo.jpg", alt: "Lamborghini interior — embroidered headrest and yellow-stitched black leather" },
        ].map((img) => (
          <img
            key={img.src}
            src={img.src}
            alt={img.alt}
            loading="lazy"
            className="aspect-[4/3] w-full border border-teal-line object-cover"
          />
        ))}
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="process-section">
    <Reveal>
      <p className="kicker">The standard</p>
      <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl">
        Four steps. No shortcuts.
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-px border border-teal-line bg-teal-line sm:grid-cols-2 lg:grid-cols-4">
      {PROCESS.map((p, i) => (
        <Reveal key={p.num} delay={i * 0.08} className="bg-obsidian transition-colors duration-300 hover:bg-panel">
          <img
            src={["/assets/inspect.jpg", "/assets/correct.jpg", "/assets/protect.jpg", "/assets/deliver.jpg"][i]}
            alt={["Detailer inspecting a black BMW M2 with a handheld light", "Dual-action polisher correcting gloss black paint", "Paint protection film being squeegeed onto a black BMW", "Finished black BMW M4 front-on under studio lights"][i]}
            loading="lazy"
            className="aspect-[16/9] w-full object-cover"
          />
          <div className="p-8">
            <span className="font-mono text-sm text-mint">{p.num}</span>
            <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight">{p.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-chrome">{p.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const QuoteServices = () => (
  <section className="border-y border-teal-line bg-panel/40" data-testid="quote-services-section">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <Reveal>
        <p className="kicker">Considered work</p>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold uppercase tracking-tight sm:text-5xl">
          Protection worth quoting properly
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        <Reveal className="group relative overflow-hidden border border-teal-line bg-obsidian">
          <img src="/assets/ceramic.jpg" alt="Ceramic coating finish on dark paint in the studio" loading="lazy" className="aspect-[16/8] w-full object-cover" />
          <div className="p-8">
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight">Ceramic Coating</h3>
            <p className="mt-3 text-sm leading-relaxed text-chrome">
              Paint correction first, then premium ceramic coatings, wheel coatings and interior fabric protection.
              Quoted per vehicle, after we see the paint.
            </p>
            <Link to="/services/ceramic-coating" data-testid="home-ceramic-link" className="group/link mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mint">
              Explore ceramic coating <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="group relative overflow-hidden border border-teal-line bg-obsidian">
          <img src={IMAGES.ppf} alt="Paint protection film application in progress on a dark vehicle" loading="lazy" className="aspect-[16/8] w-full object-cover" />
          <div className="p-8">
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight">PPF & Vinyl Wraps</h3>
            <p className="mt-3 text-sm leading-relaxed text-chrome">
              Paint protection film and wraps, consultation-based. Measured, cut and fitted for your car and how you drive it.
            </p>
            <Link to="/services/paint-protection-film" data-testid="home-ppf-link" className="group/link mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-mint">
              Explore PPF & wraps <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" aria-hidden="true" />
            </Link>
          </div>
        </Reveal>
      </div>
      <Reveal className="mt-6 flex flex-wrap gap-x-8 gap-y-3 border border-teal-line bg-obsidian px-8 py-6">
        {QUOTE_SERVICES.slice(2).map((s) => (
          <Link key={s.id} to={`/contact?service=${s.id}`} data-testid={`home-quote-${s.id}`} className="font-mono text-xs uppercase tracking-[0.2em] text-chrome transition-colors duration-200 hover:text-mint">
            {s.name} — on quote
          </Link>
        ))}
      </Reveal>
    </div>
  </section>
);

const Reviews = () => (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32" data-testid="reviews-section">
    <Reveal>
      <ReviewsCarousel />
    </Reveal>
  </section>
);

const TrustSlots = () => (
  <section className="border-t border-teal-line" data-testid="trust-section">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <Reveal>
        <p className="kicker">Trust, made visible</p>
        <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl">
          The guarantee is the lighting
        </h2>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-chrome sm:text-base">
          Every car leaves under the same inspection lighting we work under. If the finish isn't right,
          it doesn't leave the bay. That's the whole guarantee.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        <img src={IMAGES.team} alt="Luxpress detailer steam-cleaning a leather interior" loading="lazy" className="aspect-[4/3] w-full border border-teal-line object-cover object-top" />
        <img src={IMAGES.studio} alt="Machine polishing a black car in the Luxpress studio" loading="lazy" className="aspect-[4/3] w-full border border-teal-line object-cover" />
        <img src="/assets/ppf-detail.jpg" alt="Paint protection film being squeegeed onto a black BMW side skirt under studio lights" loading="lazy" className="aspect-[4/3] w-full border border-teal-line object-cover" />
      </div>
    </div>
  </section>
);

const ClosingCTA = () => (
  <section className="relative overflow-hidden border-t border-teal-line" data-testid="closing-cta">
    <img
      src={IMAGES.deliver}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover opacity-25"
    />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 py-24 text-left sm:px-8 sm:py-32">
      <Reveal>
        <p className="kicker">Ready when you are</p>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-6xl">
          Bring us the car.<br /><span className="text-mint">We'll bring the mirror.</span>
        </h2>
        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Link to="/contact" data-testid="closing-quote-cta" className="btn-primary">Request a quote</Link>
          <a href={BUSINESS.phoneHref} data-testid="closing-call-cta" className="btn-ghost">Call {BUSINESS.phoneDisplay}</a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  usePageMeta(
    "Luxpress Detailing — Premium Car Detailing, South Melbourne",
    "Paint correction, ceramic coating and premium detailing at 476 City Road, South Melbourne. Real pricing, 5.0-star rated, open daily."
  );
  return (
    <>
      <Hero />
      <ProofStrip />
      <Marquee text="Quality you see. Results you notice." />
      <ServicesOverview />
      <Transformation />
      <Process />
      <QuoteServices />
      <FlyerSection />
      <Reviews />
      <TrustSlots />
      <ClosingCTA />
    </>
  );
};

export default Home;
